import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'primereact/checkbox';
import { useHistory } from 'react-router-dom';
import { mapPurchaseInvoice, validateSameCurrency, validateSameNipt } from '../OpenBanking/PaymentOrder/PaymentOrderUtils';
import func from '../../services/functions';
import mainAxios from '../../services/mainAxios';

const usePaymentOrderMultiselect = ({
  invoices = [],
  selectedCompany,
  user,
}) => {
  const { t } = useTranslation('translations');
  const history = useHistory();
  const [selectedPaymentOrderRows, setSelectedPaymentOrderRows] = useState([]);
  const [actionLoadingPaymentOrder, setActionLoadingPaymentOrder] = useState(false);


  const getPurchaseInvoiceById = async (id) => {
      return mainAxios
        .post("endpoint/v2/getPurchaseInvoiceById", {
          value: id,
        })
        .then((res) => {
          const content = res?.data?.content?.[0]
          if(content){
            return content
          }
          return null
        });
  }

  const handleCreatePaymentOrderDraft = useCallback(async () => {
    if (!selectedPaymentOrderRows || selectedPaymentOrderRows.length === 0) {
      toast.warning(t('paymentOrder.noInvoicesSelected'), {
        containerId: 'all_toast',
      });
      return;
    }

    if (!validateSameNipt(selectedPaymentOrderRows)) {
      toast.error(t('paymentOrder.differentNipt'), {
        containerId: 'all_toast',
      });
      return;
    }

    if (!validateSameCurrency(selectedPaymentOrderRows)) {
      toast.error(t('paymentOrder.differentCurrency'), {
        containerId: 'all_toast',
      });
      return;
    }

    const paymentOrderRecords = selectedPaymentOrderRows.map(invoice =>
      mapPurchaseInvoice(invoice)
    );

    const firstInvoice =await getPurchaseInvoiceById(selectedPaymentOrderRows[0]?.id) ?? selectedPaymentOrderRows[0]


    const supplierNipt = firstInvoice?.sellerTin;
    const currency = firstInvoice.purchaseInvoiceExtracted?.currency ?? firstInvoice?.currency;
    const sellerIban = firstInvoice?.purchaseInvoiceExtracted?.purchaseInvoiceBankAccounts?.[0]?.purchaseBankAccount?.bankAccountId ?? "";
    const creditorAgent = firstInvoice?.purchaseInvoiceExtracted?.purchaseInvoiceBankAccounts?.[0]?.bank?.swift ?? "";
    const creditorBankName = (firstInvoice?.purchaseInvoiceExtracted?.purchaseInvoiceBankAccounts?.[0]?.bank?.alias || "").trim();    

    const sellerBankAccountTransactional = {
      sellerName: firstInvoice?.connectionTransactional?.name || "",
      sellerNipt: supplierNipt,
      sellerIban,
      creditorBankName,
      creditorAccountReference: "",
      creditorAgent,
      creditorAccountReferenceCurrency: currency || "ALL",
    };

    history.push({
      pathname: '/create-payment-order',
      state: {
        paymentOrderRecords,
        supplierNipt,
        currency,
        selectedInvoices: selectedPaymentOrderRows,
        sellerBankAccountTransactional,
      }
    });
  }, [selectedPaymentOrderRows, t, history]);

  const paymentOrderMultiSelectFilter = useMemo(() => {
    const selectableInvoices = (invoices ?? []).filter(
      (invoice) => invoice?.paymentType === 'NONCASH' || invoice?.purchaseStatus !== 'PAID'
    );

    if (selectableInvoices.length === 0) return <></>;

    const selectedRowIds = selectedPaymentOrderRows.map(row => row.id);
    const isChecked = selectableInvoices.every(invoice =>
      selectedRowIds.includes(invoice.id)
    );

    return (
      <Checkbox
        onChange={(e) => {
          if (e.checked) {
            if (!validateSameCurrency(selectableInvoices)) {
              toast.error(t('paymentOrder.differentCurrency'), {
                containerId: 'all_toast',
              });
              return;
            }
            setSelectedPaymentOrderRows(selectableInvoices);
          } else {
            setSelectedPaymentOrderRows([]);
          }
        }}
        checked={isChecked}
      />
    );
  }, [invoices, selectedPaymentOrderRows, t]);

  const paymentOrderMultiSelectBody = useCallback(
    (invoice) => {
      if (invoice?.paymentType !== 'NONCASH' || invoice?.purchaseStatus === 'PAID') {
        return <></>;
      }
      return (
        <Checkbox
          onChange={(e) => {
            const isCurrentlySelected = selectedPaymentOrderRows.find(row => row.id === invoice?.id);
            if (isCurrentlySelected) {
              setSelectedPaymentOrderRows((state) => state.filter(row => row.id !== invoice?.id));
              return;
            }
            const updatedState = [...selectedPaymentOrderRows, invoice];
            if (!validateSameCurrency(updatedState)) {
              toast.error(t('paymentOrder.differentCurrency'), {
                containerId: 'all_toast',
              });
              return;
            }
            setSelectedPaymentOrderRows(updatedState);
          }}
          checked={!!selectedPaymentOrderRows.find(row => row.id === invoice?.id)}
        />
      );
    },
    [selectedPaymentOrderRows, t]
  );

  const selectedCurrency = selectedPaymentOrderRows?.[0]?.currency || 'ALL';

  const totalSelectedAmount = useMemo(() => {
    return selectedPaymentOrderRows.reduce((sum, invoice) => {
      const amount = invoice?.currency === 'ALL' ? (invoice?.amountInAll || 0) : (invoice?.amount || 0);
      return sum + amount;
    }, 0);
  }, [selectedPaymentOrderRows]);

  const paymentOrderActionButtons = selectedPaymentOrderRows.length > 0 && (
    <div className="d-flex align-items-center justify-content-between" style={{ marginRight: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px',marginRight:10 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: '#28a745',marginRight:10 }}>
          {t('paymentOrder.totalValue')}: {func.getFormattedMoneyValue(totalSelectedAmount)} {selectedCurrency}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button
          className="btn btn-sm btn-primary rounded-pill mr-2"
          onClick={handleCreatePaymentOrderDraft}
          disabled={actionLoadingPaymentOrder || selectedPaymentOrderRows.length === 0}
        >
          {actionLoadingPaymentOrder ? t('paymentOrder.creatingPaymentOrder') : `${t('paymentOrder.createDraft')} (${selectedPaymentOrderRows.length})`}
        </button>
      </div>
      <button
          className="btn btn-sm btn-outline-primary rounded-pill"
          onClick={() => setSelectedPaymentOrderRows([])}
          disabled={actionLoadingPaymentOrder}
        >
          {t('common.clear')}
      </button>
    </div>
  );

  return {
    selectedPaymentOrderRows,
    setSelectedPaymentOrderRows,
    paymentOrderMultiSelectFilter,
    paymentOrderMultiSelectBody,
    paymentOrderActionButtons,
    handleCreatePaymentOrderDraft,
    actionLoadingPaymentOrder,
    validateSameNipt,
    validateSameCurrency,
  };
};

export default usePaymentOrderMultiselect;
